/**
 * The model-option math behind the traits menu, kept apart from the menu.
 *
 * Every value here is derived from provider capabilities and the stored model
 * options, so it can be read and tested without mounting a menu. The component
 * keeps the parts that only make sense while rendering.
 */

import {
  type ClaudeModelOptions,
  type CodexModelOptions,
  type ProviderKind,
  type ProviderModelOptions,
  type ServerProviderModel,
} from "@cmmd-forge/contracts";
import {
  isClaudeUltrathinkPrompt,
  trimOrNull,
  getDefaultContextWindow,
  hasContextWindowOption,
  resolveEffort,
} from "@cmmd-forge/shared/model";
import { getProviderModelCapabilities } from "../../providerModels";

export type ProviderOptions = ProviderModelOptions[ProviderKind];

function getRawEffort(
  provider: ProviderKind,
  modelOptions: ProviderOptions | null | undefined,
): string | null {
  if (provider === "codex") {
    return trimOrNull((modelOptions as CodexModelOptions | undefined)?.reasoningEffort);
  }
  return trimOrNull((modelOptions as ClaudeModelOptions | undefined)?.effort);
}

function getRawContextWindow(
  provider: ProviderKind,
  modelOptions: ProviderOptions | null | undefined,
): string | null {
  if (provider === "claudeAgent") {
    return trimOrNull((modelOptions as ClaudeModelOptions | undefined)?.contextWindow);
  }
  return null;
}

export function buildNextOptions(
  provider: ProviderKind,
  modelOptions: ProviderOptions | null | undefined,
  patch: Record<string, unknown>,
): ProviderOptions {
  if (provider === "codex") {
    return { ...(modelOptions as CodexModelOptions | undefined), ...patch } as CodexModelOptions;
  }
  return { ...(modelOptions as ClaudeModelOptions | undefined), ...patch } as ClaudeModelOptions;
}

export function getSelectedTraits(
  provider: ProviderKind,
  models: ReadonlyArray<ServerProviderModel>,
  model: string | null | undefined,
  prompt: string,
  modelOptions: ProviderOptions | null | undefined,
  allowPromptInjectedEffort: boolean,
) {
  const caps = getProviderModelCapabilities(models, model, provider);
  const effortLevels = allowPromptInjectedEffort
    ? caps.reasoningEffortLevels
    : caps.reasoningEffortLevels.filter(
        (option) => !caps.promptInjectedEffortLevels.includes(option.value),
      );

  // Resolve effort from options (provider-specific key)
  const rawEffort = getRawEffort(provider, modelOptions);
  const effort = resolveEffort(caps, rawEffort) ?? null;

  // Thinking toggle (only for models that support it)
  const thinkingEnabled = caps.supportsThinkingToggle
    ? ((modelOptions as ClaudeModelOptions | undefined)?.thinking ?? true)
    : null;

  // Fast mode
  const fastModeEnabled =
    caps.supportsFastMode &&
    (modelOptions as { fastMode?: boolean } | undefined)?.fastMode === true;

  // Context window
  const contextWindowOptions = caps.contextWindowOptions;
  const rawContextWindow = getRawContextWindow(provider, modelOptions);
  const defaultContextWindow = getDefaultContextWindow(caps);
  const contextWindow =
    rawContextWindow && hasContextWindowOption(caps, rawContextWindow)
      ? rawContextWindow
      : defaultContextWindow;

  // Prompt-controlled effort (e.g. ultrathink in prompt text)
  const ultrathinkPromptControlled =
    allowPromptInjectedEffort &&
    caps.promptInjectedEffortLevels.length > 0 &&
    isClaudeUltrathinkPrompt(prompt);

  // Check if "ultrathink" appears in the body text (not just our prefix)
  const ultrathinkInBodyText =
    ultrathinkPromptControlled && isClaudeUltrathinkPrompt(prompt.replace(/^Ultrathink:\s*/i, ""));

  return {
    caps,
    effort,
    effortLevels,
    thinkingEnabled,
    fastModeEnabled,
    contextWindowOptions,
    contextWindow,
    defaultContextWindow,
    ultrathinkPromptControlled,
    ultrathinkInBodyText,
  };
}

export type SelectedTraits = ReturnType<typeof getSelectedTraits>;

/** The effort, ultrathink or thinking segment, whichever the model exposes. */
function resolveEffortSegment(traits: SelectedTraits): string | null {
  if (traits.ultrathinkPromptControlled) return "Ultrathink";
  const effortLabel = traits.effort
    ? (traits.effortLevels.find((level) => level.value === traits.effort)?.label ?? traits.effort)
    : null;
  if (effortLabel) return effortLabel;
  if (traits.thinkingEnabled === null) return null;
  return `Thinking ${traits.thinkingEnabled ? "On" : "Off"}`;
}

/** The context window segment, shown only when the choice differs from the default. */
function resolveContextWindowSegment(traits: SelectedTraits): string | null {
  if (traits.contextWindowOptions.length <= 1) return null;
  if (traits.contextWindow === traits.defaultContextWindow) return null;
  return (
    traits.contextWindowOptions.find((option) => option.value === traits.contextWindow)?.label ??
    null
  );
}

/**
 * The one-line summary the closed menu shows.
 *
 * A trigger whose only content is a chevron has no accessible name, and every
 * segment can come back empty for a model whose sole option is a context window
 * already sitting at its default. Naming the control itself is what keeps that
 * button reachable.
 */
export function buildTraitsTriggerLabel(traits: SelectedTraits): string {
  const label = [
    resolveEffortSegment(traits),
    traits.caps.supportsFastMode && traits.fastModeEnabled ? "Fast" : null,
    resolveContextWindowSegment(traits),
  ]
    .filter(Boolean)
    .join(" · ");
  return label.length > 0 ? label : "Model options";
}
